import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControlLabel, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { request } from './OperationsProvider';
import brand from '../shell/brand';

// The response fields Quadrant needs, and what each one is for. Editing these paths is
// what makes a different VTS work without a code change.
const FIELDS = [
  ['deviceId', 'Device identifier', 'Matched against the provider device id stored on each source'],
  ['latitude', 'Latitude', 'Required'],
  ['longitude', 'Longitude', 'Required'],
  ['fixTime', 'Fix time', 'When the device recorded the position'],
  ['speed', 'Speed', 'Knots; used for speed-limit rules'],
  ['course', 'Course', 'Heading in degrees'],
  ['accuracy', 'Accuracy', 'Metres'],
  ['deviceTime', 'Device time', 'Optional'],
  ['serverTime', 'Server time', 'Used to judge how stale a position is'],
  ['valid', 'Valid flag', 'Optional'],
  ['motion', 'Motion', 'Drives "moving now" counts'],
  ['ignition', 'Ignition', 'Optional'],
  ['alarm', 'Alarm', 'powerCut / tampering raise a tamper exception'],
];

const TrackingProviderPanel = () => {
  const [form, setForm] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null); const [notice, setNotice] = useState(null); const [result, setResult] = useState(null);

  const load = async () => {
    const provider = await request('/api/operations/tracking/provider');
    setForm({
      url: provider.url || '',
      authHeader: provider.authHeader || '',
      itemsPath: provider.itemsPath || '',
      devicesPath: provider.devicesPath || '',
      allowedDeviceIds: (provider.allowedDeviceIds || []).join(', '),
      enabled: provider.enabled !== false,
      mapping: provider.mapping || {},
      hasSecret: !!provider.hasSecret,
      fromEnvironment: !!provider.fromEnvironment,
      configured: !!provider.configured || !!provider.url,
      secret: '',
    });
  };
  useEffect(() => { load().catch((e) => setError(e.message)); }, []);

  const act = async (fn) => {
    setPending(true); setError(null); setNotice(null);
    try { await fn(); } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const save = () => act(async () => {
    await request('/api/operations/tracking/provider', 'PUT', {
      url: form.url,
      secret: form.secret,
      authHeader: form.authHeader,
      itemsPath: form.itemsPath,
      devicesPath: form.devicesPath,
      enabled: String(form.enabled),
      allowedDeviceIds: form.allowedDeviceIds
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((n) => Number.isFinite(n) && n > 0),
      mapping: form.mapping,
    });
    setNotice('Connection saved. It takes effect on the next poll — no restart needed.');
    setResult(null);
    await load();
  });

  const test = () => act(async () => { setResult(await request('/api/operations/tracking/provider/test', 'POST', {})); });

  if (!form) return <Typography color="text.secondary">Loading tracking connection…</Typography>;
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setPath = (key, value) => setForm((f) => ({ ...f, mapping: { ...f.mapping, [key]: value } }));

  return (
    <Stack gap={2}>
      <Typography color="text.secondary">
        {`Where vehicle positions come from, and how this provider's response maps onto ${brand.name}'s position fields. Changes apply on the next poll.`}
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      {form.fromEnvironment && (
        <Alert severity="info">
          This workspace is connected through a deployment-configured provider. Saving here replaces it with a connection you control.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2}>
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" sx={{ flex: 1 }}>Connection</Typography>
            <Chip size="small" color={form.configured ? 'success' : 'default'} label={form.configured ? 'Connected' : 'Not connected'} variant="outlined" />
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />} label="Enabled" />
          </Stack>
          <TextField
            label="Provider base URL"
            value={form.url}
            onChange={(e) => set('url', e.target.value)}
            required
            helperText="An external provider must use HTTPS and resolve to a public address. Only the bundled tracker may use a private address."
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              fullWidth
              label={form.hasSecret ? 'New token (blank keeps the current one)' : 'API token'}
              type="password"
              autoComplete="new-password"
              value={form.secret}
              onChange={(e) => set('secret', e.target.value)}
            />
            <TextField fullWidth label="Auth header (blank = Bearer)" value={form.authHeader} onChange={(e) => set('authHeader', e.target.value)} helperText="e.g. X-API-Key" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField fullWidth label="Positions list path" value={form.itemsPath} onChange={(e) => set('itemsPath', e.target.value)} helperText="Blank if the response is already an array; e.g. data.positions" />
            <TextField fullWidth label="Devices path" value={form.devicesPath} onChange={(e) => set('devicesPath', e.target.value)} helperText="Default api/devices" />
          </Stack>
          <TextField label="Allowed device ids" value={form.allowedDeviceIds} onChange={(e) => set('allowedDeviceIds', e.target.value)} helperText="Comma separated. Leave blank to allow every device this connection can see." />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6">Response mapping</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Dotted path into each position record. Leave a row blank to ignore that field.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {FIELDS.map(([key, title, hint]) => (
            <TextField key={key} size="small" label={title} value={form.mapping[key] || ''} onChange={(e) => setPath(key, e.target.value)} helperText={hint} />
          ))}
        </Box>
      </Paper>

      <Stack direction="row" gap={2}>
        <Button variant="contained" onClick={save} disabled={pending}>Save connection</Button>
        <Button onClick={test} disabled={pending || !form.configured}>Test &amp; preview mapping</Button>
      </Stack>

      {result && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6">{`Received ${result.received} position${result.received === 1 ? '' : 's'}`}</Typography>
          {result.warning && <Alert severity="warning" sx={{ my: 1 }}>{result.warning}</Alert>}
          {(result.preview || []).length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 1 }}>The provider returned no positions to preview.</Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>{['Device', 'Latitude', 'Longitude', 'Speed', 'Fix time'].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {result.preview.map((row) => (
                  <TableRow key={`${row.deviceId}-${row.fixTime}`}>
                    <TableCell>{row.deviceId}</TableCell>
                    <TableCell>{row.latitude}</TableCell>
                    <TableCell>{row.longitude}</TableCell>
                    <TableCell>{row.speed}</TableCell>
                    <TableCell>{row.fixTime || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="caption" color="text.secondary">These rows are what the mapping produces. If latitude and longitude are zero, the paths do not match this provider&apos;s response.</Typography>
        </Paper>
      )}
    </Stack>
  );
};

export default TrackingProviderPanel;
